import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import IMAGES from '../constants/images';
import { companyInfo } from '../data/mockData';

const Hero = () => {
  const { t } = useTranslation();
  const [animationStep, setAnimationStep] = useState(0);

  useEffect(() => {
    // Sequential animation for "Clean. Protect. Renew."
    const timer1 = setTimeout(() => setAnimationStep(1), 300);
    const timer2 = setTimeout(() => setAnimationStep(2), 600);
    const timer3 = setTimeout(() => setAnimationStep(3), 900);
    const timer4 = setTimeout(() => setAnimationStep(4), 1200);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
    };
  }, []);

  const scrollToServices = () => {
    const servicesSection = document.querySelector('#services-section');
    if (servicesSection) {
      servicesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="relative min-h-[500px] sm:min-h-[600px] lg:min-h-[700px] overflow-hidden bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-900/10 to-blue-600/5"></div>

      {/* Content Container */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-[500px] sm:min-h-[600px] lg:min-h-[700px] px-4 py-12">

        {/* Company Logo */}
        <div className="mb-8 sm:mb-10 animate-fade-in">
          <img
            src={IMAGES.logos.main}
            alt="A.P. Pacific Inc. Logo"
            className="w-48 sm:w-64 md:w-80 lg:w-96 h-auto object-contain drop-shadow-lg"
          />
        </div>

        {/* Slogan - Animated Words */}
        <div className="mb-6 sm:mb-8 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-800 leading-tight">
            <span
              className={`inline-block transition-all duration-500 ${
                animationStep >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              {t('hero.slogan1')}
            </span>
            {' '}
            <span
              className={`inline-block transition-all duration-500 ${
                animationStep >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              {t('hero.slogan2')}
            </span>
            {' '}
            <span
              className={`inline-block transition-all duration-500 ${
                animationStep >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              {t('hero.slogan3')}
            </span>
          </h1>
          <p
            className={`text-xl sm:text-2xl md:text-3xl text-gray-700 mt-3 sm:mt-4 transition-all duration-500 delay-300 ${
              animationStep >= 4 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            {t('hero.tagline')}
          </p>
        </div>

        {/* Subheadline */}
        <div
          className={`max-w-3xl mx-auto mb-8 sm:mb-10 transition-all duration-700 delay-500 ${
            animationStep >= 4 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <p className="text-base sm:text-lg md:text-xl text-gray-600 text-center leading-relaxed px-4">
            {t('hero.subheadline')}
          </p>
        </div>

        {/* Call-to-Action Buttons */}
        <div
          className={`flex flex-col sm:flex-row gap-4 sm:gap-6 transition-all duration-700 delay-700 ${
            animationStep >= 4 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          {/* Primary CTA */}
          <a
            href="/contact"
            className="inline-block bg-blue-600 text-white font-bold text-base sm:text-lg px-8 sm:px-10 py-3 sm:py-4 rounded-full hover:bg-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 text-center"
          >
            {t('hero.ctaPrimary')}
          </a>

          {/* Secondary CTA */}
          <button
            onClick={scrollToServices}
            className="inline-block bg-white text-blue-600 font-bold text-base sm:text-lg px-8 sm:px-10 py-3 sm:py-4 rounded-full border-2 border-blue-600 hover:bg-blue-50 transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105"
          >
            {t('hero.ctaSecondary')}
          </button>
        </div>

        {/* Service Areas - Optional subtle mention */}
        <div
          className={`mt-8 sm:mt-10 text-center transition-all duration-700 delay-1000 ${
            animationStep >= 4 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <p className="text-sm sm:text-base text-gray-500">
            {t('hero.proudlyServing')} {companyInfo.serviceAreas.join(', ')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Hero;
